import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { walkiesClient } from "../api/walkiesClient";
import type { WalkerSessionDto } from "../types/auth";
import type {
  BookingProposalDto,
  ConversationMessageDto,
  ConversationRebookRequestDto,
  ConversationThreadDto,
  WalkerConversationListItemDto,
} from "../types/conversations";

type LoadState = "idle" | "loading" | "loaded" | "error";
type SubmitState = "idle" | "submitting" | "submitted" | "error";

type InboxItem = WalkerConversationListItemDto & Record<string, any>;
type RebookRequest = ConversationRebookRequestDto & Record<string, any>;
type Thread = ConversationThreadDto & Record<string, any>;
type Proposal = BookingProposalDto & Record<string, any>;
type Message = ConversationMessageDto & Record<string, any>;

function formatDateTime(value?: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDate(value?: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function asArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  return [];
}

function firstNonEmptyString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (isNonEmptyString(value)) return value.trim();
  }
  return undefined;
}

function firstNumber(...values: unknown[]): number | undefined {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
      const n = Number(value);
      if (Number.isFinite(n)) return n;
    }
  }
  return undefined;
}

function toTime(value?: string | null): number {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : 0;
}

function getConversationId(item?: InboxItem | null): number | undefined {
  if (!item) return undefined;
  return firstNumber(
    item.conversationId,
    item.conversationThreadId,
    item.threadId,
    item.id
  );
}

function getConversationToken(item?: InboxItem | null): string | undefined {
  if (!item) return undefined;
  return firstNonEmptyString(
    item.token,
    item.conversationToken,
    item.threadToken
  );
}

function getConversationTitle(item?: InboxItem | null): string {
  if (!item) return "Conversation";
  return (
    firstNonEmptyString(
      item.ownerName,
      item.petName,
      item.subject,
      item.title,
      item.headline
    ) ?? "Conversation"
  );
}

function getItemLastActivityUtc(item?: InboxItem | null): string | undefined {
  if (!item) return undefined;
  return firstNonEmptyString(
    item.lastMessageUtc,
    item.lastActivityUtc,
    item.updatedUtc,
    item.createdUtc
  );
}

function getItemPreview(item?: InboxItem | null): string {
  if (!item) return "";
  return (
    firstNonEmptyString(
      item.lastMessagePreview,
      item.lastMessageText,
      item.preview,
      item.snippet,
      item.ownerNeed,
      item.requestMessage
    ) ?? ""
  );
}

function getItemOwnerName(item?: InboxItem | null): string {
  if (!item) return "Owner";
  return firstNonEmptyString(item.ownerName, item.name, item.contactName) ?? "Owner";
}

function getItemOwnerEmail(item?: InboxItem | null): string {
  if (!item) return "";
  return firstNonEmptyString(item.ownerEmail, item.email) ?? "";
}

function getItemStatus(item?: InboxItem | null): string {
  if (!item) return "";
  return firstNonEmptyString(item.status, item.conversationStatus) ?? "";
}

function getThreadMessages(thread?: Thread | null): Message[] {
  if (!thread) return [];
  return asArray<Message>(
    thread.messages ??
      thread.conversationMessages ??
      thread.items ??
      thread.threadMessages
  );
}

function getThreadProposals(thread?: Thread | null): Proposal[] {
  if (!thread) return [];
  return asArray<Proposal>(
    thread.bookingProposals ??
      thread.proposals ??
      thread.itemsProposals
  );
}

function getActiveProposal(thread?: Thread | null): Proposal | undefined {
  const proposals = getThreadProposals(thread);
  if (!proposals.length) return undefined;

  const payable = proposals.find((p) =>
    ["sent", "checkoutcreated"].includes(
      String(p.status ?? "").trim().toLowerCase()
    )
  );
  if (payable) return payable;

  return [...proposals].sort(
    (a, b) =>
      toTime(
        firstNonEmptyString(
          b.updatedUtc,
          b.createdUtc,
          b.sentUtc,
          b.proposedUtc
        )
      ) -
      toTime(
        firstNonEmptyString(
          a.updatedUtc,
          a.createdUtc,
          a.sentUtc,
          a.proposedUtc
        )
      )
  )[0];
}

function extractRebookRequests(item: InboxItem): RebookRequest[] {
  const direct = item.activeRebookRequest;
  const singular = item.rebookRequest;
  const plural = item.rebookRequests;
  const fallback =
    item.conversationRebookRequest ?? item.conversationRebookRequests;

  const all = [
    ...(direct ? [direct] : []),
    ...(singular ? [singular] : []),
    ...asArray<RebookRequest>(plural),
    ...(fallback ? (Array.isArray(fallback) ? fallback : [fallback]) : []),
  ];

  return all.filter(Boolean);
}

function isActiveRebookStatus(status?: string | null): boolean {
  const s = String(status ?? "").trim().toLowerCase();
  return s === "active" || s === "pending" || s === "open" || s === "requested";
}

function getLatestActiveRebook(
  item?: InboxItem | null
): RebookRequest | undefined {
  if (!item) return undefined;

  const active = extractRebookRequests(item).filter(
    (r) =>
      !r.dismissedUtc &&
      !r.proposalCreatedUtc &&
      isActiveRebookStatus(r.status)
  );

  if (!active.length) return undefined;

  return [...active].sort((a, b) => {
    const bt = Math.max(
      toTime(b.updatedUtc),
      toTime(b.requestedUtc),
      toTime(b.proposalCreatedUtc)
    );
    const at = Math.max(
      toTime(a.updatedUtc),
      toTime(a.requestedUtc),
      toTime(a.proposalCreatedUtc)
    );
    return bt - at;
  })[0];
}

function dedupeInbox(items: InboxItem[]): InboxItem[] {
  const map = new Map<string, InboxItem>();

  for (const item of items) {
    const key =
      String(getConversationId(item) ?? "") ||
      String(getConversationToken(item) ?? "") ||
      JSON.stringify(item);

    const existing = map.get(key);
    if (!existing) {
      map.set(key, item);
      continue;
    }

    const existingRebook = getLatestActiveRebook(existing);
    const incomingRebook = getLatestActiveRebook(item);

    if (incomingRebook && !existingRebook) {
      map.set(key, item);
      continue;
    }

    if (!incomingRebook && existingRebook) {
      continue;
    }

    const existingTime = toTime(getItemLastActivityUtc(existing));
    const incomingTime = toTime(getItemLastActivityUtc(item));

    if (incomingTime >= existingTime) {
      map.set(key, item);
    }
  }

  return Array.from(map.values()).sort(
    (a, b) => toTime(getItemLastActivityUtc(b)) - toTime(getItemLastActivityUtc(a))
  );
}

function selectPriorityRebook(
  items: InboxItem[]
): { item: InboxItem; rebook: RebookRequest } | null {
  const candidates = items
    .map((item) => {
      const rebook = getLatestActiveRebook(item);
      if (!rebook) return null;
      return { item, rebook };
    })
    .filter(Boolean) as { item: InboxItem; rebook: RebookRequest }[];

  if (!candidates.length) return null;

  candidates.sort((a, b) => {
    const bt = Math.max(
      toTime(b.rebook.updatedUtc),
      toTime(b.rebook.requestedUtc),
      toTime(getItemLastActivityUtc(b.item))
    );
    const at = Math.max(
      toTime(a.rebook.updatedUtc),
      toTime(a.rebook.requestedUtc),
      toTime(getItemLastActivityUtc(a.item))
    );
    return bt - at;
  });

  return candidates[0];
}

function getMessageText(message: Message): string {
  return (
    firstNonEmptyString(
      message.messageText,
      message.body,
      message.text,
      message.content
    ) ?? ""
  );
}

function getMessageSenderLabel(message: Message): string {
  const role = firstNonEmptyString(
    message.senderType,
    message.authorType,
    message.role
  )?.toLowerCase();

  if (role === "walker") return "You";
  if (role === "owner") return "Owner";

  return (
    firstNonEmptyString(
      message.senderName,
      message.authorName,
      message.displayName
    ) ?? "Message"
  );
}

function isWalkerMessage(message: Message): boolean {
  const role = firstNonEmptyString(
    message.senderType,
    message.authorType,
    message.role
  )?.toLowerCase();

  return role === "walker" || message.isFromWalker === true;
}

function isSameInboxItem(a?: InboxItem | null, b?: InboxItem | null): boolean {
  const aId = getConversationId(a);
  const bId = getConversationId(b);

  if (aId && bId) return aId === bId;

  const aToken = getConversationToken(a);
  const bToken = getConversationToken(b);

  return !!aToken && !!bToken && aToken === bToken;
}

const client = walkiesClient as any;

async function loadInboxFromClient(): Promise<InboxItem[]> {
  const response =
    (await client.getWalkerInbox?.()) ??
    (await client.getWalkerConversationInbox?.()) ??
    (await client.getWalkerConversationList?.()) ??
    (await client.getWalkerMessages?.()) ??
    (await client.getWalkerThreads?.());

  const items =
    response?.items ??
    response?.conversations ??
    response?.threads ??
    response?.results ??
    response;

  return dedupeInbox(asArray<InboxItem>(items));
}

async function loadThreadFromClient(item: InboxItem): Promise<Thread | null> {
  const token = getConversationToken(item);
  const conversationId = getConversationId(item);

  if (token) {
    const byToken =
      (await client.getConversationThread?.(token)) ??
      (await client.getWalkerConversationThread?.(token)) ??
      (await client.getConversationByToken?.(token)) ??
      (await client.getWalkerConversationByToken?.(token));

    if (byToken) return byToken as Thread;
  }

  if (conversationId) {
    const byId =
      (await client.getConversationThreadById?.(conversationId)) ??
      (await client.getWalkerConversationThreadById?.(conversationId)) ??
      (await client.getConversation?.(conversationId));

    if (byId) return byId as Thread;
  }

  return null;
}

async function sendReplyFromClient(
  item: InboxItem,
  messageText: string
): Promise<void> {
  const token = getConversationToken(item);
  const conversationId = getConversationId(item);

  if (client.sendWalkerReply) {
    await client.sendWalkerReply({
      token,
      conversationId,
      messageText,
      message: messageText,
    });
    return;
  }

  if (client.replyToConversation) {
    await client.replyToConversation({
      token,
      conversationId,
      messageText,
      message: messageText,
    });
    return;
  }

  if (client.sendConversationReply) {
    await client.sendConversationReply({
      token,
      conversationId,
      messageText,
      message: messageText,
    });
    return;
  }

  throw new Error("No matching reply method found on walkiesClient.");
}

async function createProposalFromRebookClient(
  rebookRequestId: number
): Promise<void> {
  if (client.createProposalFromRebookRequest) {
    await client.createProposalFromRebookRequest(rebookRequestId);
    return;
  }

  if (client.createConversationProposalFromRebookRequest) {
    await client.createConversationProposalFromRebookRequest(rebookRequestId);
    return;
  }

  if (client.createRebookProposal) {
    await client.createRebookProposal(rebookRequestId);
    return;
  }

  throw new Error("No matching rebook proposal method found on walkiesClient.");
}

async function dismissRebookFromClient(rebookRequestId: number): Promise<void> {
  if (client.dismissRebookRequest) {
    await client.dismissRebookRequest(rebookRequestId);
    return;
  }

  if (client.dismissConversationRebookRequest) {
    await client.dismissConversationRebookRequest(rebookRequestId);
    return;
  }

  if (client.dismissRebook) {
    await client.dismissRebook(rebookRequestId);
    return;
  }

  throw new Error("No matching rebook dismiss method found on walkiesClient.");
}

function findInboxItemByToken(items: InboxItem[], token?: string | null): InboxItem | null {
  if (!token) return null;
  const normalized = token.trim().toLowerCase();
  if (!normalized) return null;

  return (
    items.find((item) => {
      const itemToken = getConversationToken(item);
      return !!itemToken && itemToken.trim().toLowerCase() === normalized;
    }) ?? null
  );
}

function findInboxItemByConversationId(
  items: InboxItem[],
  conversationId?: number
): InboxItem | null {
  if (!conversationId || conversationId <= 0) return null;

  return items.find((item) => getConversationId(item) === conversationId) ?? null;
}

function useCompactLayout(breakpoint = 920): boolean {
  const getValue = useCallback(
    () => (typeof window !== "undefined" ? window.innerWidth < breakpoint : false),
    [breakpoint]
  );

  const [compact, setCompact] = useState<boolean>(() => getValue());

  useEffect(() => {
    const onResize = () => setCompact(getValue());
    setCompact(getValue());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [getValue]);

  return compact;
}

const WalkerMessagesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isCompact = useCompactLayout();

  const tokenFromQuery = searchParams.get("token");
  const conversationIdFromQuery = firstNumber(searchParams.get("conversationId"));
  const focusFromQuery = (searchParams.get("focus") ?? "").trim().toLowerCase();

  const [session, setSession] = useState<WalkerSessionDto | null>(null);

  const [loadState, setLoadState] = useState<LoadState>("idle");
  const [threadLoadState, setThreadLoadState] = useState<LoadState>("idle");
  const [replyState, setReplyState] = useState<SubmitState>("idle");
  const [rebookActionState, setRebookActionState] = useState<SubmitState>("idle");

  const [error, setError] = useState("");
  const [replyError, setReplyError] = useState("");
  const [rebookError, setRebookError] = useState("");

  const [items, setItems] = useState<InboxItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<InboxItem | null>(null);
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
  const [replyText, setReplyText] = useState("");
  const [showConversationDuringRebook, setShowConversationDuringRebook] =
    useState(false);
  const [mobileView, setMobileView] = useState<"list" | "thread">("list");

  const priorityRebook = useMemo(() => selectPriorityRebook(items), [items]);
  const activeRebookItem = priorityRebook?.item ?? null;
  const activeRebook = priorityRebook?.rebook ?? null;
  const hasPriorityRebook = !!activeRebook && !!activeRebookItem;

  const visibleInboxItems = useMemo(() => {
    if (!hasPriorityRebook) return items;

    return items.filter((x) => {
      const id = getConversationId(x);
      const priorityId = getConversationId(activeRebookItem);
      if (id && priorityId) return id !== priorityId;

      const token = getConversationToken(x);
      const priorityToken = getConversationToken(activeRebookItem);
      return token !== priorityToken;
    });
  }, [items, hasPriorityRebook, activeRebookItem]);

  const activeProposal = useMemo(
    () => getActiveProposal(selectedThread),
    [selectedThread]
  );

  const unreadCount = useMemo(
    () => items.reduce((sum, item) => sum + (item.unreadCount ?? 0), 0),
    [items]
  );

  const selectedHasActiveRebook = useMemo(
    () => !!getLatestActiveRebook(selectedItem ?? undefined),
    [selectedItem]
  );

  const messages = useMemo(
    () => getThreadMessages(selectedThread),
    [selectedThread]
  );

  const summaryText = useMemo(() => {
    if (hasPriorityRebook) {
      return "A book again request needs attention first.";
    }

    if (unreadCount > 0) {
      return `${unreadCount} new ${unreadCount === 1 ? "message" : "messages"} to review.`;
    }

    return "Review messages and reply when ready.";
  }, [hasPriorityRebook, unreadCount]);

  const loadInbox = useCallback(async () => {
    setLoadState("loading");
    setError("");

    try {
      const [me, inbox] = await Promise.all([
        walkiesClient.getWalkerMe(),
        loadInboxFromClient(),
      ]);

      setSession(me);
      setItems(inbox);

      const matchedFromConversationId = findInboxItemByConversationId(
        inbox,
        conversationIdFromQuery
      );

      const matchedFromToken = findInboxItemByToken(inbox, tokenFromQuery);
      const matchedFromQuery = matchedFromConversationId ?? matchedFromToken;
      const priority = selectPriorityRebook(inbox);

      if (matchedFromQuery) {
        setSelectedItem(matchedFromQuery);

        if (focusFromQuery === "conversation") {
          setShowConversationDuringRebook(true);
          if (isCompact) setMobileView("thread");
        } else if (focusFromQuery === "rebook") {
          setShowConversationDuringRebook(false);
          if (isCompact) setMobileView("list");
        } else {
          const shouldShowConversation = !getLatestActiveRebook(matchedFromQuery);
          setShowConversationDuringRebook(shouldShowConversation);
          if (isCompact) {
            setMobileView(shouldShowConversation ? "thread" : "list");
          }
        }
      } else if (priority) {
        setSelectedItem(priority.item);
        setShowConversationDuringRebook(false);
        if (isCompact) setMobileView("list");
      } else if (inbox.length) {
        setSelectedItem((current) => {
          if (!current) return inbox[0];

          const currentId = getConversationId(current);
          const currentToken = getConversationToken(current);

          const match =
            inbox.find((x) => {
              const id = getConversationId(x);
              const token = getConversationToken(x);

              return (
                (currentId && id === currentId) ||
                (currentToken && token === currentToken)
              );
            }) ?? inbox[0];

          return match;
        });

        setShowConversationDuringRebook(true);
        if (isCompact) setMobileView("list");
      } else {
        setSelectedItem(null);
        if (isCompact) setMobileView("list");
      }

      setLoadState("loaded");
    } catch (err: any) {
      setLoadState("error");
      setError(err?.message ?? "Could not load walker inbox.");
      navigate("/walker/access", { replace: true });
    }
  }, [navigate, tokenFromQuery, conversationIdFromQuery, focusFromQuery, isCompact]);

  const loadThread = useCallback(async (item: InboxItem | null) => {
    if (!item) {
      setSelectedThread(null);
      return;
    }

    setThreadLoadState("loading");
    setReplyError("");

    try {
      const thread = await loadThreadFromClient(item);
      setSelectedThread(thread);
      setThreadLoadState("loaded");
    } catch (err: any) {
      setThreadLoadState("error");
      setSelectedThread(null);
      setReplyError(err?.message ?? "Could not load conversation.");
    }
  }, []);

  useEffect(() => {
    void loadInbox();
  }, [loadInbox]);

  useEffect(() => {
    if (!selectedItem) return;

    if (hasPriorityRebook && !showConversationDuringRebook) {
      const selectedId = getConversationId(selectedItem);
      const priorityId = getConversationId(activeRebookItem);

      if (selectedId && priorityId && selectedId === priorityId) {
        setSelectedThread(null);
        return;
      }
    }

    void loadThread(selectedItem);
  }, [selectedItem, hasPriorityRebook, showConversationDuringRebook, loadThread, activeRebookItem]);

  useEffect(() => {
    if (!isCompact) {
      setMobileView("list");
    }
  }, [isCompact]);

  const handleSelectItem = useCallback(
    (item: InboxItem) => {
      setSelectedItem(item);

      const pickedRebook = getLatestActiveRebook(item);
      if (pickedRebook) {
        setShowConversationDuringRebook(false);
        if (isCompact) setMobileView("list");
      } else {
        setShowConversationDuringRebook(true);
        if (isCompact) setMobileView("thread");
      }
    },
    [isCompact]
  );

  const handleOpenConversation = useCallback(
    (item: InboxItem) => {
      setSelectedItem(item);
      setShowConversationDuringRebook(true);
      if (isCompact) setMobileView("thread");
    },
    [isCompact]
  );

  const handleSendReply = useCallback(async () => {
    if (!selectedItem) return;

    const messageText = replyText.trim();
    if (!messageText) {
      setReplyError("Please enter a message.");
      return;
    }

    setReplyState("submitting");
    setReplyError("");

    try {
      await sendReplyFromClient(selectedItem, messageText);
      setReplyText("");
      setReplyState("submitted");
      await loadThread(selectedItem);
      await loadInbox();
      setReplyState("idle");
    } catch (err: any) {
      setReplyState("error");
      setReplyError(err?.message ?? "Could not send reply.");
    }
  }, [replyText, selectedItem, loadThread, loadInbox]);

  const handleCreateProposal = useCallback(async () => {
    if (!activeRebook) return;

    const rebookRequestId = firstNumber(
      activeRebook.rebookRequestId,
      activeRebook.id
    );

    if (!rebookRequestId) {
      setRebookError("Rebook request id is missing.");
      return;
    }

    setRebookActionState("submitting");
    setRebookError("");

    try {
      await createProposalFromRebookClient(rebookRequestId);
      await loadInbox();

      const refreshedPriority = selectPriorityRebook(await loadInboxFromClient());

      if (!refreshedPriority && activeRebookItem) {
        setSelectedItem(activeRebookItem);
        setShowConversationDuringRebook(true);
        await loadThread(activeRebookItem);
        if (isCompact) setMobileView("thread");
      }

      setRebookActionState("idle");
    } catch (err: any) {
      setRebookActionState("error");
      setRebookError(err?.message ?? "Could not create proposal.");
    }
  }, [activeRebook, activeRebookItem, loadThread, loadInbox, isCompact]);

  const handleDismissRebook = useCallback(async () => {
    if (!activeRebook) return;

    const rebookRequestId = firstNumber(
      activeRebook.rebookRequestId,
      activeRebook.id
    );

    if (!rebookRequestId) {
      setRebookError("Rebook request id is missing.");
      return;
    }

    setRebookActionState("submitting");
    setRebookError("");

    try {
      await dismissRebookFromClient(rebookRequestId);
      await loadInbox();
      setRebookActionState("idle");
    } catch (err: any) {
      setRebookActionState("error");
      setRebookError(err?.message ?? "Could not dismiss rebook request.");
    }
  }, [activeRebook, loadInbox]);

  async function handleSignOut() {
    await walkiesClient.signOutWalker();
    navigate("/");
  }

  const renderPriorityRebook = () => {
    if (!hasPriorityRebook || !activeRebook || !activeRebookItem) return null;

    const selectedIsPriority = isSameInboxItem(selectedItem, activeRebookItem);

    if (!selectedIsPriority && conversationIdFromQuery && focusFromQuery === "conversation") {
      return null;
    }

    return (
      <section style={styles.priorityCard}>
        <div style={styles.priorityTop}>
          <div style={styles.priorityBadge}>Next priority</div>
          <div style={styles.priorityTitle}>Book again request</div>
          <div style={styles.priorityMeta}>
            From {getItemOwnerName(activeRebookItem)}
            {getItemOwnerEmail(activeRebookItem)
              ? ` · ${getItemOwnerEmail(activeRebookItem)}`
              : ""}
          </div>
          {activeRebook.requestedUtc ? (
            <div style={styles.priorityMetaSoft}>
              Requested {formatDateTime(activeRebook.requestedUtc)}
            </div>
          ) : null}
        </div>

        <div style={styles.priorityMessageBlock}>
          <div style={styles.priorityMessageLabel}>Owner message</div>
          {activeRebook.requestMessage ? (
            <div style={styles.priorityMessage}>{activeRebook.requestMessage}</div>
          ) : (
            <div style={styles.priorityMessageMuted}>
              No message was included with this request.
            </div>
          )}
        </div>

        <div style={styles.priorityActionStack}>
          <button
            type="button"
            style={styles.ctaButton}
            onClick={handleCreateProposal}
            disabled={rebookActionState === "submitting"}
          >
            {rebookActionState === "submitting"
              ? "Creating proposal..."
              : "Create proposal"}
          </button>

          <button
            type="button"
            style={styles.supportButton}
            onClick={() => handleOpenConversation(activeRebookItem)}
          >
            Open conversation
          </button>

          <button
            type="button"
            style={styles.linkButton}
            onClick={handleDismissRebook}
            disabled={rebookActionState === "submitting"}
          >
            Dismiss this request
          </button>
        </div>

        {rebookError ? <div style={styles.errorBox}>{rebookError}</div> : null}

        {!showConversationDuringRebook ? (
          <div style={styles.focusNote}>
            This request is shown first so the next action is clear.
          </div>
        ) : null}
      </section>
    );
  };

  const renderInboxItem = (item: InboxItem, index: number) => {
    const active = isSameInboxItem(selectedItem, item);
    const hasRebook = !!getLatestActiveRebook(item);
    const unread = firstNumber(item.unreadCount) ?? 0;

    return (
      <button
        key={`${getConversationId(item) ?? "c"}-${getConversationToken(item) ?? index}`}
        type="button"
        onClick={() => handleSelectItem(item)}
        style={{
          ...styles.conversationCard,
          ...(active ? styles.conversationCardActive : {}),
        }}
      >
        <div style={styles.conversationHeaderRow}>
          <div style={styles.conversationHeaderMain}>
            <div style={styles.conversationName}>{getItemOwnerName(item)}</div>
            <div style={styles.conversationTitle}>{getConversationTitle(item)}</div>
          </div>

          <div style={styles.conversationHeaderSide}>
            {unread > 0 ? (
              <div style={styles.unreadPill}>
                {unread} new
              </div>
            ) : null}
            {hasRebook ? <div style={styles.rebookPill}>Rebook</div> : null}
          </div>
        </div>

        <div style={styles.conversationPreview}>
          {getItemPreview(item) || getItemStatus(item) || "Open conversation"}
        </div>

        <div style={styles.conversationFooter}>
          <span>{formatDate(getItemLastActivityUtc(item)) || "No recent date"}</span>
          <span style={styles.conversationOpenHint}>
            {hasRebook ? "Open request" : "Open chat"}
          </span>
        </div>
      </button>
    );
  };

  const renderInbox = () => {
    if (!visibleInboxItems.length) {
      return <div style={styles.emptyPanel}>No other conversations to show.</div>;
    }

    return (
      <div style={styles.conversationList}>
        {visibleInboxItems.map((item, index) => renderInboxItem(item, index))}
      </div>
    );
  };

  const renderThreadContent = () => {
    if (!selectedItem) {
      return <div style={styles.emptyPanel}>Select a conversation.</div>;
    }

    if (threadLoadState === "loading") {
      return <div style={styles.emptyPanel}>Loading conversation...</div>;
    }

    if (threadLoadState === "error") {
      return (
        <div style={styles.emptyPanelError}>
          {replyError || "Could not load conversation."}
        </div>
      );
    }

    return (
      <div>
        <div style={styles.threadHeader}>
          <div style={styles.threadHeaderTop}>
            {isCompact ? (
              <button
                type="button"
                style={styles.backButton}
                onClick={() => setMobileView("list")}
              >
                Back to messages
              </button>
            ) : null}

            {selectedHasActiveRebook && hasPriorityRebook ? (
              <button
                type="button"
                style={styles.smallSupportButton}
                onClick={() => {
                  setShowConversationDuringRebook(false);
                  if (isCompact) setMobileView("list");
                }}
              >
                Show request
              </button>
            ) : null}
          </div>

          <div style={styles.threadOwnerName}>{getItemOwnerName(selectedItem)}</div>
          <div style={styles.threadTitle}>{getConversationTitle(selectedItem)}</div>

          <div style={styles.threadMeta}>
            {getItemOwnerEmail(selectedItem)
              ? getItemOwnerEmail(selectedItem)
              : "Conversation details"}
          </div>
        </div>

        {activeProposal ? (
          <div style={styles.infoCard}>
            <div style={styles.infoCardLabel}>Current proposal</div>
            <div style={styles.infoCardText}>
              Status: {firstNonEmptyString(activeProposal.status, "Unknown")}
              {firstNonEmptyString(
                activeProposal.startUtc,
                activeProposal.proposedStartUtc
              )
                ? ` · ${formatDateTime(
                    firstNonEmptyString(
                      activeProposal.startUtc,
                      activeProposal.proposedStartUtc
                    )
                  )}`
                : ""}
            </div>
          </div>
        ) : null}

        {messages.length ? (
          <div style={styles.messageList}>
            {messages.map((message, index) => {
              const walker = isWalkerMessage(message);

              return (
                <div
                  key={`${firstNumber(message.messageId, message.id) ?? index}`}
                  style={{
                    ...styles.messageBubble,
                    ...(walker ? styles.messageBubbleYou : styles.messageBubbleThem),
                  }}
                >
                  <div style={styles.messageSender}>
                    {getMessageSenderLabel(message)}
                    {firstNonEmptyString(
                      message.sentUtc,
                      message.createdUtc,
                      message.timestampUtc
                    )
                      ? ` · ${formatDateTime(
                          firstNonEmptyString(
                            message.sentUtc,
                            message.createdUtc,
                            message.timestampUtc
                          )
                        )}`
                      : ""}
                  </div>

                  <div style={styles.messageText}>{getMessageText(message)}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={styles.emptyThreadText}>No conversation messages found.</div>
        )}

        <div style={styles.replyPanel}>
          <label htmlFor="walker-reply" style={styles.replyLabel}>
            Your reply
          </label>

          <textarea
            id="walker-reply"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            style={styles.textarea}
            placeholder="Type your message here"
          />

          <div style={styles.replyActions}>
            <button
              type="button"
              style={styles.ctaButton}
              onClick={handleSendReply}
              disabled={replyState === "submitting"}
            >
              {replyState === "submitting" ? "Sending..." : "Send reply"}
            </button>

            {activeProposal ? (
              <Link to="/walker/bookings" style={styles.inlineActionLink}>
                View bookings
              </Link>
            ) : null}
          </div>

          {replyError ? <div style={styles.errorBox}>{replyError}</div> : null}
        </div>
      </div>
    );
  };

  const renderDesktopContent = () => {
    if (hasPriorityRebook && !showConversationDuringRebook) {
      return (
        <div style={styles.singleColumnStack}>
          <div style={styles.sectionCard}>
            <div style={styles.sectionTitle}>Other conversations</div>
            {renderInbox()}
          </div>
        </div>
      );
    }

    return (
      <div style={styles.desktopGrid}>
        <div style={styles.sectionCard}>
          <div style={styles.sectionTitle}>
            {hasPriorityRebook ? "Other conversations" : "Messages"}
          </div>
          {renderInbox()}
        </div>

        <div style={styles.sectionCard}>
          {renderThreadContent()}
        </div>
      </div>
    );
  };

  const renderMobileContent = () => {
    const showThreadPanel =
      mobileView === "thread" &&
      !!selectedItem &&
      (!hasPriorityRebook || showConversationDuringRebook || !selectedHasActiveRebook);

    if (showThreadPanel) {
      return <div style={styles.singleColumnStack}>{renderThreadContent()}</div>;
    }

    return (
      <div style={styles.singleColumnStack}>
        <div style={styles.sectionCard}>
          <div style={styles.sectionTitle}>
            {hasPriorityRebook ? "Other conversations" : "Messages"}
          </div>
          {renderInbox()}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.page}>
      <div style={styles.outer}>
        <div style={styles.pageCard}>
          <header style={styles.headerBlock}>
            <div style={styles.headerTopRow}>
              <div>
                <div style={styles.pageBadge}>Walker dashboard</div>
                <h1 style={styles.pageTitle}>Messages</h1>
                <p style={styles.pageSubtitle}>
                  {session?.firstName
                    ? `Hello ${session.firstName}. ${summaryText}`
                    : summaryText}
                </p>
              </div>

              {unreadCount > 0 ? (
                <div style={styles.headerNotice}>
                  {unreadCount} new {unreadCount === 1 ? "message" : "messages"}
                </div>
              ) : null}
            </div>

            <div style={styles.navGrid}>
              <button
                type="button"
                style={styles.navButton}
                onClick={() => navigate("/walker/dashboard")}
              >
                Dashboard
              </button>

              <button
                type="button"
                style={styles.navButtonActive}
                onClick={() => navigate("/walker/messages")}
              >
                Messages
              </button>

              <button
                type="button"
                style={styles.navButton}
                onClick={() => navigate("/walker/bookings")}
              >
                Bookings
              </button>

              <button
                type="button"
                style={styles.navButton}
                onClick={handleSignOut}
              >
                Sign out
              </button>
            </div>
          </header>

          {loadState === "loading" ? (
            <div style={styles.sectionCard}>Loading inbox...</div>
          ) : null}

          {loadState === "error" ? (
            <div style={styles.sectionCard}>
              <div style={styles.errorBox}>{error || "Could not load inbox."}</div>
              <div style={{ marginTop: 14 }}>
                <button
                  type="button"
                  style={styles.supportButton}
                  onClick={() => void loadInbox()}
                >
                  Retry
                </button>
              </div>
            </div>
          ) : null}

          {loadState === "loaded" ? (
            <>
              {renderPriorityRebook()}
              {isCompact ? renderMobileContent() : renderDesktopContent()}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(180deg, #244239 0%, #1b312b 100%)",
    padding: "16px 12px 32px",
    color: "#10201c",
  },

  outer: {
    maxWidth: 1200,
    margin: "0 auto",
  },

  pageCard: {
    background: "#f6f1e8",
    borderRadius: 24,
    padding: 16,
    boxShadow: "0 18px 48px rgba(0,0,0,0.16)",
  },

  headerBlock: {
    marginBottom: 16,
  },

  headerTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 16,
  },

  pageBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 34,
    padding: "6px 12px",
    borderRadius: 999,
    background: "rgba(36, 66, 57, 0.08)",
    color: "#244239",
    fontWeight: 800,
    fontSize: 14,
    marginBottom: 10,
  },

  pageTitle: {
    margin: "0 0 8px",
    fontSize: 34,
    lineHeight: 1.1,
    fontWeight: 900,
    color: "#17312a",
  },

  pageSubtitle: {
    margin: 0,
    fontSize: 17,
    lineHeight: 1.55,
    color: "rgba(16, 32, 28, 0.78)",
    maxWidth: 720,
  },

  headerNotice: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 42,
    padding: "8px 14px",
    borderRadius: 999,
    background: "#fff3db",
    border: "1px solid rgba(242, 138, 22, 0.22)",
    color: "#9a5609",
    fontWeight: 800,
    fontSize: 15,
  },

  navGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
    gap: 10,
  },

  navButton: {
    minHeight: 52,
    padding: "12px 14px",
    borderRadius: 16,
    border: "1px solid rgba(36, 66, 57, 0.12)",
    background: "#ffffff",
    color: "#17312a",
    fontWeight: 800,
    fontSize: 16,
    cursor: "pointer",
  },

  navButtonActive: {
    minHeight: 52,
    padding: "12px 14px",
    borderRadius: 16,
    border: "none",
    background: "#f28a16",
    color: "#ffffff",
    fontWeight: 900,
    fontSize: 16,
    cursor: "pointer",
  },

  priorityCard: {
    background: "linear-gradient(180deg, #fff7e8 0%, #fff1d2 100%)",
    border: "1px solid rgba(242, 138, 22, 0.24)",
    borderRadius: 22,
    padding: 18,
    marginBottom: 16,
  },

  priorityTop: {
    marginBottom: 14,
  },

  priorityBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 34,
    padding: "6px 12px",
    borderRadius: 999,
    background: "rgba(242, 138, 22, 0.12)",
    border: "1px solid rgba(242, 138, 22, 0.22)",
    color: "#9a5609",
    fontWeight: 800,
    fontSize: 14,
    marginBottom: 12,
  },

  priorityTitle: {
    fontSize: 30,
    lineHeight: 1.1,
    fontWeight: 900,
    color: "#17312a",
    marginBottom: 8,
  },

  priorityMeta: {
    fontSize: 17,
    lineHeight: 1.5,
    color: "#17312a",
    fontWeight: 700,
  },

  priorityMetaSoft: {
    marginTop: 4,
    fontSize: 15,
    lineHeight: 1.5,
    color: "rgba(16, 32, 28, 0.7)",
  },

  priorityMessageBlock: {
    background: "#ffffff",
    border: "1px solid rgba(242, 138, 22, 0.14)",
    borderRadius: 18,
    padding: 16,
  },

  priorityMessageLabel: {
    fontSize: 14,
    fontWeight: 800,
    color: "rgba(16, 32, 28, 0.7)",
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },

  priorityMessage: {
    fontSize: 17,
    lineHeight: 1.65,
    color: "#10201c",
    whiteSpace: "pre-wrap",
  },

  priorityMessageMuted: {
    fontSize: 17,
    lineHeight: 1.65,
    color: "rgba(16, 32, 28, 0.62)",
  },

  priorityActionStack: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 10,
    marginTop: 16,
  },

  ctaButton: {
    minHeight: 56,
    width: "100%",
    padding: "14px 18px",
    borderRadius: 16,
    border: "none",
    background: "#f28a16",
    color: "#ffffff",
    fontWeight: 900,
    fontSize: 18,
    cursor: "pointer",
  },

  supportButton: {
    minHeight: 52,
    width: "100%",
    padding: "12px 18px",
    borderRadius: 16,
    border: "1px solid rgba(36, 66, 57, 0.12)",
    background: "#244239",
    color: "#ffffff",
    fontWeight: 800,
    fontSize: 17,
    cursor: "pointer",
  },

  linkButton: {
    minHeight: 48,
    width: "100%",
    padding: "10px 14px",
    borderRadius: 14,
    border: "1px solid rgba(36, 66, 57, 0.12)",
    background: "#ffffff",
    color: "#17312a",
    fontWeight: 800,
    fontSize: 16,
    cursor: "pointer",
  },

  focusNote: {
    marginTop: 14,
    borderRadius: 16,
    padding: 14,
    background: "rgba(36, 66, 57, 0.05)",
    border: "1px dashed rgba(36, 66, 57, 0.14)",
    color: "rgba(16, 32, 28, 0.78)",
    fontSize: 15,
    lineHeight: 1.55,
  },

  singleColumnStack: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },

  desktopGrid: {
    display: "grid",
    gridTemplateColumns: "360px minmax(0, 1fr)",
    gap: 16,
    alignItems: "start",
  },

  sectionCard: {
    background: "#fcfaf6",
    border: "1px solid rgba(36, 66, 57, 0.08)",
    borderRadius: 22,
    padding: 16,
  },

  sectionTitle: {
    fontSize: 22,
    lineHeight: 1.2,
    fontWeight: 900,
    color: "#17312a",
    marginBottom: 14,
  },

  conversationList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  conversationCard: {
    width: "100%",
    textAlign: "left",
    border: "1px solid rgba(36, 66, 57, 0.1)",
    borderRadius: 18,
    padding: 16,
    background: "#ffffff",
    cursor: "pointer",
  },

  conversationCardActive: {
    border: "1px solid rgba(242, 138, 22, 0.34)",
    boxShadow: "0 0 0 3px rgba(242, 138, 22, 0.10)",
  },

  conversationHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
    marginBottom: 10,
  },

  conversationHeaderMain: {
    minWidth: 0,
    flex: 1,
  },

  conversationHeaderSide: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 8,
    flexShrink: 0,
  },

  conversationName: {
    fontSize: 18,
    lineHeight: 1.3,
    fontWeight: 900,
    color: "#17312a",
    marginBottom: 2,
  },

  conversationTitle: {
    fontSize: 15,
    lineHeight: 1.4,
    color: "rgba(16, 32, 28, 0.72)",
    fontWeight: 700,
  },

  conversationPreview: {
    fontSize: 16,
    lineHeight: 1.6,
    color: "#24302c",
    marginBottom: 12,
    whiteSpace: "pre-wrap",
  },

  conversationFooter: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    fontSize: 14,
    color: "rgba(16, 32, 28, 0.62)",
  },

  conversationOpenHint: {
    fontWeight: 800,
    color: "#244239",
  },

  unreadPill: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 28,
    padding: "4px 10px",
    borderRadius: 999,
    background: "rgba(36, 66, 57, 0.08)",
    color: "#244239",
    fontWeight: 800,
    fontSize: 13,
  },

  rebookPill: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 28,
    padding: "4px 10px",
    borderRadius: 999,
    background: "rgba(242, 138, 22, 0.12)",
    border: "1px solid rgba(242, 138, 22, 0.22)",
    color: "#9a5609",
    fontWeight: 800,
    fontSize: 13,
  },

  threadHeader: {
    borderBottom: "1px solid rgba(36, 66, 57, 0.08)",
    paddingBottom: 14,
    marginBottom: 16,
  },

  threadHeaderTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: 10,
  },

  backButton: {
    minHeight: 44,
    padding: "8px 12px",
    borderRadius: 12,
    border: "1px solid rgba(36, 66, 57, 0.12)",
    background: "#ffffff",
    color: "#17312a",
    fontWeight: 800,
    fontSize: 15,
    cursor: "pointer",
  },

  smallSupportButton: {
    minHeight: 44,
    padding: "8px 12px",
    borderRadius: 12,
    border: "1px solid rgba(36, 66, 57, 0.12)",
    background: "#244239",
    color: "#ffffff",
    fontWeight: 800,
    fontSize: 15,
    cursor: "pointer",
  },

  threadOwnerName: {
    fontSize: 16,
    fontWeight: 800,
    color: "rgba(16, 32, 28, 0.72)",
    marginBottom: 4,
  },

  threadTitle: {
    fontSize: 30,
    lineHeight: 1.15,
    fontWeight: 900,
    color: "#17312a",
    marginBottom: 6,
  },

  threadMeta: {
    fontSize: 16,
    lineHeight: 1.5,
    color: "rgba(16, 32, 28, 0.72)",
  },

  infoCard: {
    border: "1px solid rgba(37, 99, 235, 0.14)",
    background: "rgba(37, 99, 235, 0.05)",
    borderRadius: 16,
    padding: 14,
    marginBottom: 16,
  },

  infoCardLabel: {
    fontSize: 14,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.3,
    color: "rgba(16, 32, 28, 0.72)",
    marginBottom: 6,
  },

  infoCardText: {
    fontSize: 16,
    lineHeight: 1.55,
    color: "#17312a",
    fontWeight: 700,
  },

  messageList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    marginBottom: 18,
  },

  messageBubble: {
    borderRadius: 18,
    padding: 14,
    maxWidth: "92%",
  },

  messageBubbleYou: {
    alignSelf: "flex-end",
    background: "#244239",
    color: "#ffffff",
  },

  messageBubbleThem: {
    alignSelf: "flex-start",
    background: "#ffffff",
    color: "#10201c",
    border: "1px solid rgba(36, 66, 57, 0.08)",
  },

  messageSender: {
    fontSize: 13,
    lineHeight: 1.4,
    marginBottom: 8,
    opacity: 0.82,
    fontWeight: 800,
  },

  messageText: {
    fontSize: 16,
    lineHeight: 1.65,
    whiteSpace: "pre-wrap",
  },

  replyPanel: {
    borderTop: "1px solid rgba(36, 66, 57, 0.08)",
    paddingTop: 16,
    marginTop: 10,
  },

  replyLabel: {
    display: "block",
    fontWeight: 900,
    fontSize: 18,
    color: "#17312a",
    marginBottom: 10,
  },

  textarea: {
    width: "100%",
    minHeight: 140,
    resize: "vertical",
    borderRadius: 16,
    border: "1px solid rgba(36, 66, 57, 0.16)",
    padding: 14,
    font: "inherit",
    fontSize: 17,
    lineHeight: 1.6,
    boxSizing: "border-box",
    background: "#ffffff",
    color: "#10201c",
  },

  replyActions: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginTop: 14,
    alignItems: "stretch",
  },

  inlineActionLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 48,
    padding: "10px 14px",
    borderRadius: 14,
    border: "1px solid rgba(36, 66, 57, 0.12)",
    background: "#ffffff",
    color: "#17312a",
    fontWeight: 800,
    fontSize: 16,
    textDecoration: "none",
  },

  emptyPanel: {
    color: "rgba(16, 32, 28, 0.68)",
    fontSize: 16,
    lineHeight: 1.6,
  },

  emptyPanelError: {
    color: "#7f1d1d",
    fontWeight: 800,
    fontSize: 16,
    lineHeight: 1.6,
  },

  emptyThreadText: {
    color: "rgba(16, 32, 28, 0.68)",
    marginBottom: 16,
    fontSize: 16,
    lineHeight: 1.6,
  },

  errorBox: {
    marginTop: 14,
    borderRadius: 16,
    padding: 14,
    background: "rgba(220, 38, 38, 0.08)",
    border: "1px solid rgba(220, 38, 38, 0.14)",
    color: "#7f1d1d",
    fontWeight: 800,
    fontSize: 15,
    lineHeight: 1.55,
  },
};

export default WalkerMessagesPage;